<template>
  <v-navigation-drawer app
                       clipped
                       width="300"
                       :mobile-breakpoint="750"
                       class="drawer-container">

    <div class="drawer-top">
      <v-img class="logo-connected" @click="home" />
      <router-link v-if="!inDashboard" to="/Dashboard" />
    </div>

    <div class="drawer-middle">
      <v-treeview ref="tree"
                  v-model:activated="activeItemIds"
                  v-model:opened="openedItemIds"
                  :items="treeItems"
                  dense
                  return-object
                  activatable
                  expand-icon="">

        <template #title="{ item }">
          <v-divider v-if="item.name === '__sep__'" />
          <div v-else class="d-flex align-center tree-title">
            <v-icon v-if="item.treeIcon" class="tree-icon me-2">{{ item.treeIcon }}</v-icon>
            <span class="tree-label">{{ item.name }}</span>
          </div>
        </template>

        <template #append="{ item, isOpen }">
          <div class="tree-append-inner">
            <span v-if="cartBadge(item) > 0" class="cart-badge">{{ cartBadge(item) }}</span>
            <v-icon v-if="item.children && item.children.length && item.name !== '__sep__'" class="tree-toggle-icon" @click.stop="toggleOpen(item)">
              {{ isOpen ? 'fas fa-minus' : 'fas fa-plus' }}
            </v-icon>
          </div>
        </template>
      </v-treeview>
    </div>

    <div class="drawer-bottom">
      <v-list>
        <v-list-item>
          <v-menu v-model="notifOpenned"
                  open-on-click
                  :close-on-content-click="false"
                  location="end"
                  content-class="notification-menu">
            <template #activator="{ props }">
              <div v-bind="props"
                   @click="props.onClick"
                   class="d-flex align-center notification-trigger">
                <v-icon class="notification-bell-icon me-2">fas fa-bell</v-icon>
                <span class="notification-bell-label">Notifications</span>
                <span v-if="unreadCount > 0"
                      class="notification-count">
                  {{ unreadCount }}
                </span>
              </div>
            </template>

            <list-notifications v-if="notificationsReady"
                                :notifications="notifList"
                                @close="closeNotifs" />
          </v-menu>
        </v-list-item>

        <v-list-item class="col-12 py-3 hover-highlight" style="font-size: 14px" @click="goToFreshdesk()">
          <v-icon size="20" class="notification-bell-icon me-2">fas fa-ticket</v-icon>
          Mes tickets
        </v-list-item>


        <v-list-item class="user-box">
          <v-menu v-if="user"
                  open-on-click
                  :close-on-content-click="true"
                  class="user-menu"
                  v-model="isUserMenuOpen"
                  location="end"
                  :offset="[ 45, 10 ]">
            <template #activator="{ props }">
              <div v-bind="props" @click="props.onClick" class="d-flex align-center user-trigger">
                <v-avatar size="32" class="me-2 user-initial-avatar">
                  <span>{{ user.prenom.charAt(0).toUpperCase() }}{{ user.nom.charAt(0).toUpperCase() }}</span>
                </v-avatar>
                <span class="user-name">{{ user.prenom.charAt(0).toUpperCase() + user.prenom.slice(1) }}</span>
                <v-icon class="arrow-icon"
                        :class="{ 'rotate-up': isUserMenuOpen }">
                  fas fa-chevron-down
                </v-icon>
              </div>
            </template>


            <div class="profile-menu-box">
              <v-col class="col-12 py-3 hover-highlight" @click="goToProfil()">
                <v-icon size="20" class="notification-bell-icon me-2">fas fa-user</v-icon>
                Mon profil
              </v-col>

              <v-col class="col-12 py-3 hover-highlight" @click="goToPreference()">
                <v-icon size="20" class="notification-bell-icon me-2">fas fa-gear</v-icon>
                Mes préférences
              </v-col>

              <profil-selector v-if="hasMultipleProfile" />

              <v-col class="col-12 py-3 hover-highlight" @click="logout">
                <v-icon size="20" class="notification-bell-icon me-2">fas fa-right-from-bracket</v-icon>
                Me déconnecter
              </v-col>
            </div>
          </v-menu>
        </v-list-item>
      </v-list>
      <div class="drawer-spacer"></div>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
  import { storeNotif, storeSession } from '@/generated/stores';
  import { CiRouter, goToModule } from '@/framework/base/helpers/NavHelper';
  import Module, { TreeItem } from '@/generated/classes/Module';
  import { Modules } from '@/generated/classes/AppSchema';
  import { defineComponent, type PropType } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import Notif from "@/generated/classes/Notif.ts";
  import { RouteBuilder } from "@/generated/router/RouteBuilder.ts";
  import AvatarImage from '@/assets/avatar.png';
  import { NotifDefTypes } from "@/generated/classes/NotifDef.ts";
  import NotificationBellWithCounter from "@/framework/notif/components/NotificationBellWithCounter.vue";
  import ListNotifications from "@/framework/notif/components/ListNotifications.vue";
  import Contact from "@/generated/classes/Contact.ts";
  import Cart from '@/extend/classes/Cart';
  import { ListFilter } from '@/extend/classes/ListData';
  import { EnumProfile } from '@/framework/base/classes/UserProfile';
  import HubServices from '@/generated/hub/HubServices';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    components: {
      NotificationBellWithCounter,
      ListNotifications,
    },
    props: {
      modules: {
        type: Array as PropType<Module[]>,
        required: true
      },
      oldNotifs: {
        type: Array as PropType<Notif[]>,
        required: true
      },
      user: {
        type: Contact,
        required: true
      },
      hasMultipleProfile: {
        type: Boolean,
        required: true
      },
      inDashboard: {
        type: Boolean,
        required: true
      }
    },
    data() {
      const lastRoute = undefined as any | undefined;
      const notifications: Notification[] = [];
      return {
        notifOpenned: false,
        activeItem: new TreeItem,
        treeMap: new Map<number, TreeItem>,
        lastRoute,
        item: -1,
        router: useRouter(),
        route: useRoute(),
        iconTicket: AvatarImage,
        notifications,
        notificationsLoading: false,
        isUserMenuOpen: false,
        openedItemIds: [],
        carts: [] as Cart[],
        cartItems: [] as TreeItem[],
        cartTotalCount: 0,
        cartChildCounts: [] as number[],
      };
    },
    mounted() {
      const user = storeSession.connectedUser
      if (user && (user.profil & EnumProfile.client) > 0) {
        Cart.list(new ListFilter()).then((data) => {
          this.carts = (data.reccords as any[]).map((c) => Cart.fromJson(c));
        }).catch(() => { });
        HubServices.onCartTagUpdated(() => this.updateCarts());
      }
    },
    beforeUnmount() {
      HubServices.connection.off('CartUpdated');
    },
    computed: {
      treeItems(): TreeItem[] {
        this.treeMap.clear();
        const treeItems: TreeItem[] = [];
        const home = new TreeItem();
        home.name = "Accueil";
        home.treeIcon = "fas fa-house";
        home.children = null;
        home.route.module = Modules.NONE;
        treeItems.push(home);
        if (this.route) {
          this.modules.forEach((module) => {
            if (module.route && !module.hideInDrawer) {
              treeItems.push(module.treeItem);
            }
          });
        }
        treeItems.push(...this.cartItems);
        this.SetTreeItemId(treeItems, 0);

        return treeItems;
      },
      activeItemIds: {
        get(): any[] {
          let found = false;
          if (!this.lastRoute || this.route.name != this.lastRoute.name) {
            this.setLastRoute(this.route);
            this.treeMap.forEach((tree, index) => {
              if (!found) {
                if (this.route.name == "Dashboard") {
                  const item = this.treeMap.get(1);
                  if (item) {
                    this.activeItem = item;
                    found = true
                  }
                }
                if (tree.route && tree.route.module.toLowerCase() == this.route.name) {
                  found = true
                  if (this.activeItem.route.module.toLowerCase() != tree.route.module.toLowerCase()) {
                    this.activeItem = tree;
                  }
                }
              }
            });
            if (!found) {
              this.setActiveItemId(0);
            }
          }
          return [this.activeItem];
        },
        set(value: any[]) {
          if (value.length > 0) {
            const item = this.treeMap.get(value[0].id);
            if (item) {
              this.setActiveItem(item);
            } else {
              this.activeItem = new TreeItem;
            }
          }
        }
      },
      selectedItem: {
        get(): number {
          let found = false;
          if (this.route) {
            this.modules.forEach((element, index) => {
              if (element.route.module.toLowerCase() == this.route.name) {
                this.setItem(index + 1);
                found = true;
              }
            });
            if (this.route.name == "Dashboard") {
              this.setItem(0);
              found = true;
            }
          }
          if (!found) {
            this.setItem(-1);
          }
          return this.item;
        },
        set(value: number) {
          //
        }
      },
      notifList(): Notif[] {
        const notifs = storeNotif.listNotif;
        const firstLoad = this.oldNotifs.length == 0;
        notifs.forEach((n) => {
          RouteBuilder.loadStore(n.route);
          if (firstLoad) {
            this.oldNotifs.push(n);
          } else if (!this.oldNotifs.includes(n)) {
            this.oldNotifs.push(n);
            this.notify(n);
          }
        })
        return notifs;
      },
      unreadCount(): number {
        const notifs = storeNotif.listNotif;
        return notifs.filter(notif => !notif.isRead).length;
      },
      notificationsReady(): boolean {
        return storeNotif.notifLoaded && !this.notificationsLoading;
      },
    },
    watch: {
      carts(newCarts: Cart[]) {
        this.cartItems = this.buildCartItems(newCarts);
      },
      notifOpenned(value: boolean) {
        if (value) {
          this.loadNotifications();
        }
      }
    },
    methods: {
      loadNotifications(): void {
        if (!storeNotif.notifLoaded && !this.notificationsLoading) {
          const filter = new ListFilter();
          filter.page = 1;
          filter.pageLimit = 20;
          const notificationsReceived = storeNotif.listNotif;
          this.notificationsLoading = true;
          storeNotif.listFilteredNotif(filter)
            .then(() => {
              notificationsReceived.forEach((notification) => {
                storeNotif.mNotifCreated(notification);
              });
            })
            .finally(() => {
              this.notificationsLoading = false;
            });
        }
      },
      updateCarts() {
        Cart.list(new ListFilter()).then((data) => {
          this.carts = (data.reccords as any[]).map((c) => Cart.fromJson(c));
        });
      },
      cartBadge(item: TreeItem): number {
        if (this.cartItems.length < 2) return 0;
        const cartRoot = this.cartItems[1];
        if (item === cartRoot) return this.cartTotalCount;
        const childIdx = (cartRoot.children ?? []).indexOf(item);
        return childIdx >= 0 ? this.cartChildCounts[childIdx] : 0;
      },
      buildCartItems(carts: Cart[]): TreeItem[] {
        const paieCount = (c: Cart) => c.cartItems.filter(i => i.objType !== ObjTypes.ACCOUNT_ENTRY).length;
        this.cartTotalCount = carts.reduce((sum, c) => sum + paieCount(c), 0);
        this.cartChildCounts = carts.map(c => paieCount(c));
        if (carts.length === 0) return [];
        const sep = new TreeItem();
        sep.name = '__sep__';
        const cartRoot = new TreeItem();
        cartRoot.name = carts.length > 1 ? 'Mes paniers' : 'Mon panier';
        cartRoot.treeIcon = 'fas fa-shopping-cart';
        cartRoot.route.module = Modules.NONE;
        if (carts.length > 1) {
          cartRoot.children = carts.map(c => {
            const child = new TreeItem();
            child.name = c.ownerLabel;
            child.treeIcon = '';
            child.route.module = Modules.NONE;
            return child;
          });
        }
        return [sep, cartRoot];
      },
      goToCart(cart?: Cart) {
        const query = cart ? { etablissementId: String(cart.ownerId) } : {};
        CiRouter.setRouteChangedSelector(null);
        CiRouter.setRoute(null);
        this.router.push({ name: Modules.CART.toLowerCase(), query });
      },
      setLastRoute(value: any) {
        this.lastRoute = Object.assign({}, value)
      },
      setActiveItemId(value: number) {
        if (value == 0) {
          this.activeItem = new TreeItem();
        } else {
          this.activeItem.id = value;
        }
      },
      setItem(value: number) {
        this.item = value;
      },
      SetTreeItemId(treeItems: TreeItem[], id: number): number {
        treeItems.forEach((item) => {
          item.id = ++id;
          this.treeMap.set(item.id, item);
          if (item.children && item.children.length > 0) {
            id = this.SetTreeItemId(item.children, id);
          } else {
            item.children = null;
          }
        });
        return id;
      },
      setActiveItem(item: TreeItem): void {
        if (item.name === '__sep__') return;
        if (this.cartItems.length > 1) {
          const cartRoot = this.cartItems[1];
          if (item === cartRoot) {
            this.goToCart();
            return;
          }
          const childIdx = (cartRoot.children ?? []).indexOf(item);
          if (childIdx >= 0) {
            this.goToCart(this.carts[childIdx]);
            return;
          }
        }
        this.activeItem = item;
        if (this.activeItem.route.module == Modules.NONE) {
          if (this.route.name != "Dashboard") {
            this.router.push({ name: "Dashboard" });
          }
        } else {
          goToModule(this.router, this.activeItem.route);
        }
      },
      notify(notif: Notif): void {
        const notification = new Notification("", {
          tag: notif.id.toString(),
          data: JSON.stringify(notif),
          body: notif.label,
          icon: this.iconTicket
        });
        notification.onclick = this.clickOnNotif;
        this.notifications.push(notification);
      },
      removeNotif(notif: Notification) {
        notif.close();
        const index = this.notifications.findIndex((x) => x == notif);
        if (index >= 0) {
          this.notifications.splice(index, 1);
        }
      },
      clickOnNotif(evt: Event) {
        const notifObj = evt.currentTarget;
        if (notifObj) {
          this.removeNotif(notifObj as Notification);
          // @ts-expect-error: notifObj.data est supposé être de type Notif
          const notif = notifObj.data as Notif;
          if (notif.type == NotifDefTypes.INFO) {
            const notifToDelete = storeNotif.getNotif(notif.id)
            notifToDelete.deleteForStoreNotif();
          }
          const route = notif.route;
          if (route) {
            CiRouter.addRoute(route);
          }
        }
      },
      goToProfil() {
        this.$emit("goToProfil");
      },
      goToPreference() {
        this.$emit("goToPreference");
      },
      goToFreshdesk() {
        this.$emit("goToFreshdesk");
      },
      logout() {
        this.$emit("logout");
      },
      home() {
        this.$emit("home");
      },
      toggleOpen(item: TreeItem) {
        const index = this.openedItemIds.indexOf(item);
        if (index === -1) {
          this.openedItemIds.push(item);
        } else {
          this.openedItemIds.splice(index, 1);
        }
      },
      closeNotifs() {
        this.notifOpenned = false;
      }
    }
  })

</script>

<style>
  .drawer-container {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .drawer-top {
    flex-shrink: 0;
    background: white;
    padding: 16px;
    z-index: 10;
  }

  .drawer-middle {
    flex: 1 1 0;
    overflow-y: auto;
    padding: 0 8px 8px 8px;
    min-height: 0;
  }

    .drawer-middle .v-treeview-item__label {
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: unset !important;
    }

  .drawer-bottom {
    flex-shrink: 0;
    padding: 8px 8px 0 8px;
    background: white;
    z-index: 10;
  }

  .drawer-container .v-navigation-drawer__content {
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    height: 100% !important;
  }

  .drawer-middle .v-list-item__prepend {
    display: none !important;
  }

  .notification-trigger {
    cursor: pointer;
  }

  .notification-bell-icon {
    font-size: 14px;
    height: 32px;
    width: 32px;
    color: #25A3A7;
  }

  .notification-bell-label {
    font-size: 14px;
    font-weight: 500;
    color: #2D3539;
  }

  .notification-count {
    background-color: #E31F1C;
    color: #FFFFFF;
    font-size: 12px;
    border-radius: 40px;
    width: 24px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 125px;
  }

  .tree-title {
    font-size: 14px;
    align-items: flex-start;
  }

  .tree-label {
    font-size: 14px;
    font-weight: 500;
    color: #2D3539;
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
  }

  .tree-icon {
    font-size: 16px;
    height: 32px;
    width: 32px;
    color: #25A3A7;
  }

  .tree-toggle-icon {
    font-size: 14px;
    width: 16px;
    height: 16px;
    padding-right: 16px;
    color: #2D3539;
  }

  .tree-append-inner {
    position: relative;
    display: flex;
    align-items: center;
  }

  .cart-badge {
    position: absolute;
    right: 28px;
    background-color: #E31F1C;
    color: #FFFFFF;
    font-size: 11px;
    font-weight: 600;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }


  .v-list-item__append {
    width: 16px;
    height: 16px;
    font-size: 12px;
  }

  .v-list-item {
    padding: 4px 16px 4px 8px !important;
  }

  .v-avatar {
    background-color: #F7F8F8 !important;
    color: #135253 !important;
    font-weight: 500;
    font-size: 14px;
    width: 32px;
    height: 32px;
    border-width: 1px;
  }

  .rotate-up {
    transform: rotate(180deg);
    transition: transform 0.3s ease;
  }

  .user-trigger {
    display: flex;
    align-items: center;
    width: 99%;
  }

  .user-name {
    font-size: 14px;
    font-weight: 500;
    color: #2D3539;
  }

  .arrow-icon {
    margin-left: auto;
    transition: transform 0.3s ease;
    font-size: 14px;
    width: 16px;
    height: 16px;
  }

  .v-col.hover-highlight {
    transition: background-color 0.2s ease;
    cursor: pointer;
    background-color: #FFFFFF !important;
  }

    .v-col.hover-highlight:hover {
      background-color: rgb(var(--v-theme-secondary90)) !important;
    }

  .user-menu {
    cursor: pointer;
  }

  .profile-menu-box {
    border-radius: 8px;
    box-shadow: 0 4px 11px 0 rgba(9, 41, 42, 0.08);
  }

  .drawer-spacer {
    height: 60px;
  }
</style>
